import { ArrowRight, Zap, Gauge, Wifi, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, Stagger, staggerItem } from "@/components/Reveal";
import Cta from "@/components/Cta";
import { IMAGES } from "@/lib/site";

const SPECS = [
  { v: "1 Gbps", l: "Peak download", icon: Zap },
  { v: "940", l: "Mbps uploads", icon: Gauge },
  { v: "0", l: "Data caps", icon: Wifi },
  { v: "24/7", l: "Local support", icon: ShieldCheck },
];

const STEPS = [
  { n: "01", t: "We beam it", d: "A small, weather-tuned receiver on your property connects to our local wireless network — no trenching, no torn-up yard." },
  { n: "02", t: "You get fiber-like speed", d: "Advanced wireless protocols deliver fast, low-latency performance that rivals buried fiber." },
  { n: "03", t: "We keep it fast", d: "Continuous network upgrades and proactive monitoring mean your connection stays ahead of demand." },
];

export default function WavFiber() {
  return (
    <>
      <Seo
        title="WAVFiber — Fiber-Speed Wireless Internet up to 1Gbps | CYAN Wireless"
        description="WAVFiber by CYAN Wireless delivers fiber-like speeds up to 1 Gbps with fast uploads and downloads, no contracts, unlimited data and price-for-life. Available in Cortez, Dolores & Yellow Jacket."
        path="/wavfiber"
      />
      <PageHero
        overline="WAVFiber"
        titleLines={["Fiber speeds.", <span key="2">Zero <span className="text-cyan-400 text-glow">cables.</span></span>]}
        subtitle="The future of rural internet is here. WAVFiber brings ultra-fast connectivity up to 1 Gbps — the performance of fiber without the wait, the trenching or the contracts."
        image={IMAGES.fiberBlue}
        imageAlt="Glowing blue fiber optic strands"
      >
        <Cta to="/survey" data-testid="wav-order-cta">Order WAVFiber <ArrowRight size={16} /></Cta>
        <Cta href="tel:9704403178" variant="secondary">Call to Order</Cta>
      </PageHero>

      <section className="py-16 bg-ink-900 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4 gap-5">
          {SPECS.map((s) => (
            <Reveal key={s.l}>
              <div className="text-center sm:text-left">
                <s.icon className="text-cyan-400 mb-3 mx-auto sm:mx-0" size={24} />
                <div className="font-display font-black text-4xl sm:text-5xl text-white tracking-tighter">{s.v}</div>
                <div className="font-mono-tech text-[11px] uppercase tracking-widest text-zinc-500 mt-1">{s.l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-ink-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-16">
            <p className="overline mb-4">How it works</p>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter text-white leading-[0.95]">Wireless, done right.</h2>
          </Reveal>
          <Stagger className="grid md:grid-cols-3 gap-6">
            {STEPS.map((s) => (
              <motion.div key={s.n} variants={staggerItem} className="bg-ink-900 border border-white/5 rounded-2xl p-8 hover:border-cyan-500/40 transition-colors">
                <div className="font-mono-tech text-cyan-400 text-lg mb-4">{s.n}</div>
                <h3 className="font-display font-bold text-2xl text-white mb-3">{s.t}</h3>
                <p className="text-zinc-400 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </Stagger>

          <Reveal className="mt-16 relative rounded-2xl overflow-hidden border border-white/10">
            <img src={IMAGES.fiberCyan} alt="Cyan fiber light" className="w-full h-64 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-ink-950 to-ink-950/40 flex items-center">
              <div className="p-8 sm:p-12 max-w-lg">
                <h3 className="font-display font-extrabold text-3xl text-white">Speed for all the ways you internet.</h3>
                <p className="text-zinc-400 mt-3">Gaming, streaming, remote work, big uploads — WAVFiber handles it all at once. <span className="text-zinc-500 text-sm">*Service and speed in select locations only.</span></p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
